import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { findFarmsByProximity } from "../../api/search-service/searchService"
import useUserLocation from "../../hooks/useUserLocation"
import FarmCardStyle from "../../assets/stylesheets/FarmCard.module.css";
import SkeletonCard  from "../common/SkeletonCard"
import SwiperFarmCardList from "./SwiperFarmCardList";
import CarouselNavigation from "../common/CarouselNavigation";
import slugify from 'slugify';

import Logger from "../../utils/logger";

const log = Logger.create("PYOCarousel");

const pyoCarouselContainerTitle = "Pick your own farms worth the trip";
const pyoCarouselContainerSubtitle = "Grab a basket and pick your own straight from the source";

export default function PYOCarousel() {
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const prevRef   = useRef(null);
  const nextRef   = useRef(null);
  const { location, error } = useUserLocation()

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd]             = useState(false);
  const [farms, setFarms]             = useState([]);
 const [loading, setLoading] = useState(true); 

  const updateNavState = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const PYO_CATEGORY_ID =  1

  // DEBUG: useUserLocation (IP/GPS) 
  useEffect(() => {
    if (!location) {
      log.log('[PYOCarousel][Location] null', { error });
      return;
    }
    const source = location?.source ?? (location?.ip ? 'IP' : 'GPS');
    log.log(`[PYOCarousel][Location] from ${source}:`, location);
  }, [location, error]); 
  
  
  // when we get the coords, call the proximity endpoint
  useEffect(() => {
      if (!location) return

      setLoading(true);     
      findFarmsByProximity({
        verbose: false,
        latitude:  location.lat, 
        longitude: location.lng,
        distance:  10000,               // e.g. 10000km radius
        categoryId: PYO_CATEGORY_ID,
        limit: 6,
        sort: 'distance',
        order: 'ASC'
      })
        .then(response => {
          const farmsArray = Array.isArray(response.items) ? response.items : [];
          setFarms(farmsArray)      
        })
        .catch(log.error)
        .finally(() => setLoading(false)); 
  }, [location])


  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;
    const onChange = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };
    swiper.on("slideChange", onChange);
    onChange();
    return () => swiper.off("slideChange", onChange);
  }, [farms]);

  if (error) return <div className="p-8 text-center">Location error: {error}</div>

  return (
    <section className={FarmCardStyle["events-section"]}>
      <div className={FarmCardStyle["events-container"]}>
        <div className={FarmCardStyle["events-header"]}>
          <div>
            {loading
              ? <SkeletonCard variant="header" />
              : (
                <>
                  <h2 className={FarmCardStyle["events-title"]}>
                    {pyoCarouselContainerTitle} &#62;
                  </h2>
                  <p className={FarmCardStyle["events-subtitle"]}>
                    {pyoCarouselContainerSubtitle}
                  </p>
                </>
              )
            }
          </div>
          <CarouselNavigation 
            prevRef={prevRef}
            nextRef={nextRef}
            isBeginning={isBeginning}
            isEnd={isEnd}
          />
        </div>

        <SwiperFarmCardList 
          farms={farms}
          loading={loading}
          onFarmClick={farm => {
            const slug = slugify(farm.name || '', { lower: true, strict: true });
            navigate(`/farm/${farm.id}/${slug}`);
          }}          skeletonCount={6}
          prevRef={prevRef}
          nextRef={nextRef}
          updateNavState={updateNavState}
        />
      </div>
    </section>
  );
}

